import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { clsx } from 'clsx'
import {
  CheckCheck, CheckSquare, Pin, Inbox, MessageSquare, PencilLine,
  BellOff, Paperclip, X, Clock3, MoonStar, Archive, ChevronDown, ChevronRight, GripVertical,
} from 'lucide-react'
import { useArchivedGroups } from '../lib/archivedGroups'
import { useQueryClient } from '@tanstack/react-query'
import { useChats, useEngagedTopics, useMarkChatRead, usePrefetchChat, useSetupManifest, useWorkMonitor, type Chat, type EngagedTopic, type WorkMonitor, type WorkMonitorItem } from '../lib/api'
import { useWsConnected } from '../lib/ws'
import { saveLaneNavigationOrder } from '../lib/laneNavigationOrder'
import { useLongPress } from '../lib/longPress'
import { useElasticOverscroll } from '../lib/useElasticOverscroll'
import { usePullToRefresh, PullIndicator } from '../lib/usePullToRefresh'
import { activeWorkStatusFriendly, laneStatusFriendly, TONE_BG } from '../lib/laneStatus'
import { hasActiveChatWork, matchesDoneChatFilter, isFreeChat, isSnoozedChat } from '../lib/chatListFilters'
import { outboxTopicKey, useOutboxPendingScopes } from '../lib/outboxPending'
import { isLongEngagedTopicWork, matchesEngagedTopicFilter } from '../lib/engagedTopicFilters'
import { chatListDirectUnreadTopicId, chatListTarget, chatTarget, isCodeChatId, sameChatRoute, TODO_CHAT_ID } from '../lib/chatNavigation'
import { preloadThreadViewComponents } from '../lib/preloadChatViews'
import { startNavTiming } from '../lib/perfTelemetry'
import AgentBadge from '../components/AgentBadge'
import CodeConversationsView from '../components/CodeConversationsView'
import CodeConversationButtons from '../components/CodeConversationButtons'
import EngagedTopicsView from '../components/EngagedTopicsView'
import EngagedTopicDoneButton from '../components/EngagedTopicDoneButton'
import {
  chatHasDraft,
  firstDraftForChat,
  isVisibleDraft,
  latestDraftAtForChat,
  type PendingShareSnapshot,
  useUi,
} from '../store/ui'
import { peekPendingShare, discardPendingShare } from '../lib/pendingShares'
import { relativeTimeFromNow, relativeUptime } from '../lib/time'

type FilterTab = 'lanes' | 'groups' | 'code'
type ChatFilter = 'all' | 'done' | 'free' | 'live' | 'snooze'
type AllView = 'chats' | 'long-work'

const AUTH_HELP_URL = ((import.meta.env.VITE_AGENTCHAT_AUTH_HELP_URL as string | undefined) ?? '').trim()
const ALL_VIEW_STORAGE_KEY = 'lane-control:chatlist-all-view'

function loadAllView(): AllView {
  try {
    return localStorage.getItem(ALL_VIEW_STORAGE_KEY) === 'long-work' ? 'long-work' : 'chats'
  } catch {/* ignore */}
  return 'chats'
}

function saveAllView(v: AllView): void {
  try { localStorage.setItem(ALL_VIEW_STORAGE_KEY, v) } catch {/* ignore */}
}

// Work-state filters describe lane runners, not group containers. A group's
// Active/Snoozed/Done state belongs to its topics and is filtered inside the
// group, so the Groups tab always shows every group.
const FILTERS_BY_TAB: Record<FilterTab, ChatFilter[]> = {
  lanes: ['all', 'done', 'free', 'live', 'snooze'],
  groups: ['all'],
  // Coding groups are containers like any other group: their work state lives
  // in their topics, so the same all-only rule applies.
  code: ['all'],
}

/** Coerce a raw ?filter= value into a filter valid for the given tab. Unknown
 *  values, and lanes-only filters on the Groups tab, collapse to 'all'. */
function normalizeFilter(tab: FilterTab, value: string | null | undefined): ChatFilter {
  return (FILTERS_BY_TAB[tab] as string[]).includes(value ?? '') ? (value as ChatFilter) : 'all'
}

// The active filter is read from the URL (?filter=), so the hardware back button
// can collapse it down to Done. localStorage only remembers the last filter the
// user PICKED per tab, used to re-seed the URL when the list is reopened without
// a ?filter= (e.g. backing out of a chat lands on the bare Lanes home).
function filterStorageKey(tab: FilterTab): string {
  return `lane-control:chatlist-filter:${tab}`
}

function loadStoredFilter(tab: FilterTab): ChatFilter {
  try {
    return normalizeFilter(tab, localStorage.getItem(filterStorageKey(tab)))
  } catch {/* ignore */}
  return 'all'
}

function saveStoredFilter(tab: FilterTab, filter: ChatFilter): void {
  try { localStorage.setItem(filterStorageKey(tab), filter) } catch {/* ignore */}
}

function pendingShareBannerText(share: PendingShareSnapshot): string {
  const fileCount = share.files.length
  const hasText = share.text.trim().length > 0
  if (fileCount === 0) return 'Instruction ready - tap a chat'
  if (fileCount === 1 && hasText) return 'File + text ready - tap a chat'
  if (fileCount === 1) return 'File ready - tap a chat'
  return `${fileCount} files${hasText ? ' + text' : ''} ready - tap a chat`
}

/** Filter predicate per tab. `chat.type` is 'user-driven' for the numbered
 * Codex/Claude lanes and 'auto-spawn' for the group chats. (The TODO tab is not
 * a list tab — it opens the Todoist group's topics directly; see the tab nav.) */
function filterChatsByTab(chats: Chat[], tab: FilterTab): Chat[] {
  if (tab === 'lanes') return chats.filter((c) => c.type !== 'auto-spawn')
  // Coding groups are auto-spawn chats but get their own tab, so they are
  // excluded from Groups to avoid listing the same chat in two places.
  if (tab === 'code') return chats.filter((c) => isCodeChatId(c.id))
  return chats.filter((c) => c.type === 'auto-spawn' && !isCodeChatId(c.id))
}

function previewDraft(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

/** WhatsApp-style preview of the latest message body. Collapses whitespace
 *  and prefixes "You: " when Mark sent it (inbound = from Mark, outbound
 *  = agent reply). Returns null when there's nothing useful to show, so
 *  the row falls back to the lane status strip alone. */
function previewMessage(
  body: string | null | undefined,
  direction: Chat['last_message_direction'],
): string | null {
  if (!body) return null
  const cleaned = body.replace(/\s+/g, ' ').trim()
  if (!cleaned) return null
  return direction === 'inbound' ? `You: ${cleaned}` : cleaned
}

function laneNumberForChat(chat: Chat): number | null {
  if (chat.type === 'auto-spawn') return null
  const match = chat.lane_id?.match(/(\d+)$/)
  return match ? parseInt(match[1], 10) : null
}

function chatDisplayTitle(chat: Chat): string {
  return chat.session_title || chat.display_name || chat.id
}

function prioritizeDraftChats(
  chats: Chat[],
  draftsByChat: Record<string, string>,
  draftUpdatedAt: Record<string, number>,
): Chat[] {
  return chats
    .map((chat, index) => ({
      chat,
      index,
      draftAt: latestDraftAtForChat(draftsByChat, draftUpdatedAt, chat.id),
    }))
    .sort((a, b) => {
      const aHasDraft = chatHasDraft(draftsByChat, a.chat.id)
      const bHasDraft = chatHasDraft(draftsByChat, b.chat.id)
      if (aHasDraft !== bHasDraft) return aHasDraft ? -1 : 1
      if (aHasDraft && bHasDraft && a.draftAt !== b.draftAt) return b.draftAt - a.draftAt
      return a.index - b.index
    })
    .map((entry) => entry.chat)
}

type GroupZone = 'main' | 'archive'

function zoneAtPoint(x: number, y: number, dragged: Element | null): GroupZone | null {
  const hit = document.elementsFromPoint(x, y).find((el) => !dragged?.contains(el))
  const zone = hit?.closest('[data-group-zone]')?.getAttribute('data-group-zone')
  return zone === 'main' || zone === 'archive' ? zone : null
}

/** Group row with a drag handle that moves it between the main list and the
 *  Archive section. Pointer events (not HTML5 DnD) so touch drags work. */
function DraggableGroupRow({
  zone,
  onDragZone,
  onDrop,
  children,
}: {
  zone: GroupZone
  onDragZone: (zone: GroupZone | null) => void
  onDrop: (zone: GroupZone) => void
  children: ReactNode
}) {
  const [dragY, setDragY] = useState<number | null>(null)
  const startYRef = useRef(0)
  const rowRef = useRef<HTMLDivElement>(null)
  return (
    <div
      ref={rowRef}
      className={clsx('relative flex items-stretch', dragY !== null && 'z-20 shadow-lg opacity-90 bg-stone-50 dark:bg-stone-900')}
      style={dragY !== null ? { transform: `translateY(${dragY}px)` } : undefined}
    >
      <button
        type="button"
        aria-label={zone === 'archive' ? 'Drag out of Archive' : 'Drag into Archive'}
        title="Drag to move between groups and Archive"
        className="shrink-0 grid place-items-center w-7 text-stone-400 border-b border-stone-100 dark:border-stone-800/60 cursor-grab active:cursor-grabbing"
        style={{ touchAction: 'none' }}
        onPointerDown={(e) => {
          e.preventDefault()
          try { e.currentTarget.setPointerCapture(e.pointerId) } catch {/* pointer already released */}
          startYRef.current = e.clientY
          setDragY(0)
        }}
        onPointerMove={(e) => {
          if (dragY === null) return
          setDragY(e.clientY - startYRef.current)
          onDragZone(zoneAtPoint(e.clientX, e.clientY, rowRef.current))
        }}
        onPointerUp={(e) => {
          if (dragY === null) return
          setDragY(null)
          onDragZone(null)
          const target = zoneAtPoint(e.clientX, e.clientY, rowRef.current)
          if (target && target !== zone) onDrop(target)
        }}
        onPointerCancel={() => { setDragY(null); onDragZone(null) }}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

function ChatRow({
  chat,
  draft,
  selectionMode = false,
  selected = false,
  onSelectGroup,
  onToggleGroup,
}: {
  chat: Chat
  draft: string | null
  selectionMode?: boolean
  selected?: boolean
  onSelectGroup?: (chat: Chat) => void
  onToggleGroup?: (chat: Chat) => void
}) {
  const wsConnected = useWsConnected()
  const status = laneStatusFriendly(chat.lane_status, chat.lane_last_heartbeat, {
    busySince: chat.lane_busy_since,
    lastProgressAt: chat.last_progress_at,
    lastProgressKind: chat.last_progress_kind,
  }, wsConnected)
  const uptime = relativeUptime(Math.max(chat.created_at, chat.last_reset_at || 0))
  const queue = chat.queue_depth ?? 0
  const unread = chat.type === 'auto-spawn'
    ? (chat.active_unread_topic_count ?? 0)
    : (chat.unread ?? 0)
  const activeTopics = chat.active_topic_count ?? 0
  const hasDraft = isVisibleDraft(draft)
  const lastActivity = chat.last_activity_at
    ? relativeTimeFromNow(chat.last_activity_at)
    : null
  const snoozedUntil = chat.is_snoozed && chat.snoozed_until
    ? relativeTimeFromNow(chat.snoozed_until)
    : null
  const lastMessage = previewMessage(chat.last_message_body, chat.last_message_direction)
  const prefetch = usePrefetchChat()
  const location = useLocation()
  const suppressNextClickRef = useRef(false)
  const selectableGroup = chat.type === 'auto-spawn'
  const directUnreadTopicId = chatListDirectUnreadTopicId(chat)
  const href = chatListTarget(chat)
  const isCurrentRoute = sameChatRoute(location.pathname, location.search, href)
  const laneNumber = laneNumberForChat(chat)
  const title = chatDisplayTitle(chat)

  // Warm the chat-detail + messages cache the moment the user TOUCHES the
  // row — by the time React Router navigates (~50–150ms later) the data is
  // usually already in cache, so MessageList renders without a spinner.
  const startPrefetch = () => {
    // In split view the selected chat row remains clickable beside its open
    // transcript. Re-warming that same scope can replace the visible message
    // model for a render and flash the Load older control, while a redundant
    // router navigation adds history churn. An active-row click is inert.
    if (isCurrentRoute) return
    if (directUnreadTopicId) {
      startNavTiming('topic-open', `${chat.id}:${directUnreadTopicId}`, {
        source: 'chat-list-single-unread',
      })
    } else {
      startNavTiming(chat.type === 'auto-spawn' ? 'group-open' : 'chat-open', chat.id, {
        source: 'chat-list',
      })
    }
    if (chat.type !== 'auto-spawn' || directUnreadTopicId) preloadThreadViewComponents()
    prefetch(chat.id, directUnreadTopicId, {
      messages: chat.type !== 'auto-spawn' || !!directUnreadTopicId,
      topicMessages: chat.type === 'auto-spawn' ? 3 : 0,
    })
  }
  const longPressBind = useLongPress((e) => {
    if (!selectableGroup) return
    e.preventDefault()
    e.stopPropagation()
    suppressNextClickRef.current = true
    onSelectGroup?.(chat)
  }, { delay: 450 })

  return (
    <>
    <Link
      to={href}
      data-lane-id={chat.type !== 'auto-spawn' ? chat.id : undefined}
      onPointerDown={(e) => {
        if (isCurrentRoute) return
        if (!selectionMode) startPrefetch()
        longPressBind.onPointerDown(e)
      }}
      onPointerMove={longPressBind.onPointerMove}
      onPointerUp={longPressBind.onPointerUp}
      onPointerCancel={longPressBind.onPointerCancel}
      onPointerLeave={longPressBind.onPointerLeave}
      onContextMenu={longPressBind.onContextMenu}
      onTouchStart={() => { if (!selectionMode && !isCurrentRoute) startPrefetch() }}
      onMouseEnter={startPrefetch}
      onFocus={startPrefetch}
      onClick={(e) => {
        if (suppressNextClickRef.current) {
          suppressNextClickRef.current = false
          e.preventDefault()
          e.stopPropagation()
          return
        }
        if (selectionMode) {
          e.preventDefault()
          e.stopPropagation()
          if (selectableGroup) onToggleGroup?.(chat)
          return
        }
        if (isCurrentRoute) {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
      aria-current={isCurrentRoute ? 'page' : undefined}
      className={clsx(
        'flex items-center gap-3 px-3 py-2.5 transition-colors',
        'hover:bg-stone-100 dark:hover:bg-stone-800/60',
        'border-b border-stone-100 dark:border-stone-800/60',
        unread > 0 && !hasDraft && 'bg-blue-50/30 dark:bg-blue-950/10',
        hasDraft && 'bg-amber-100 dark:bg-amber-900/40 border-l-4 border-l-amber-500 pl-2',
        selected && 'bg-blue-100 dark:bg-blue-950/50 ring-1 ring-inset ring-blue-500/60',
        isCurrentRoute && 'bg-stone-100 dark:bg-stone-800/60',
      )}
    >
      {selectionMode && selectableGroup && (
        <span
          className={clsx(
            'grid place-items-center w-5 h-5 rounded-full border shrink-0',
            selected
              ? 'bg-blue-600 border-blue-600 text-white'
              : 'border-stone-300 dark:border-stone-600 text-transparent',
          )}
          aria-hidden
        >
          <CheckSquare className="w-3.5 h-3.5" />
        </span>
      )}
      <div className="relative shrink-0">
        <AgentBadge
          agent={chat.uses_multiple_agents ? 'mixed' : (chat.display_agent || chat.preferred_model || chat.agent)}
          size={36}
          number={laneNumber}
        />
        <span
          className={clsx(
            'absolute -bottom-0.5 -right-0.5 inline-block w-2.5 h-2.5 rounded-full ring-2 ring-stone-50 dark:ring-stone-900',
            TONE_BG[status.tone],
            status.blink && 'animate-pulse',
          )}
          title={status.description}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {chat.pinned && (
            <Pin className="w-3 h-3 text-amber-500 shrink-0" aria-label="Pinned" />
          )}
          {chat.muted && (
            <BellOff className="w-3 h-3 text-stone-400 shrink-0" aria-label="Muted" />
          )}
          {snoozedUntil && (
            <MoonStar className="w-3 h-3 text-amber-500 shrink-0" aria-label="Snoozed" />
          )}
          <span className={clsx(
            'truncate',
            unread > 0 ? 'font-semibold' : 'font-medium',
          )} title={chat.display_name && chat.session_title ? chat.display_name : title}>
            {title}
          </span>
          {chat.pending_approval && <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 shrink-0">Approval needed</span>}
          {chat.type === 'auto-spawn' && chat.category && (
            <span className="text-[9px] uppercase tracking-wider text-stone-500 px-1.5 py-0.5 bg-stone-200 dark:bg-stone-700 rounded shrink-0">
              {chat.category}
            </span>
          )}
        </div>
        {hasDraft && draft && (
          <div className="text-[13px] text-amber-900 dark:text-amber-200 truncate flex items-center gap-1.5 font-medium">
            <PencilLine className="w-3.5 h-3.5 shrink-0" aria-hidden />
            <span className="font-bold shrink-0 uppercase tracking-wide text-[11px]">Draft</span>
            <span className="truncate italic">{previewDraft(draft)}</span>
          </div>
        )}
        {!hasDraft && lastMessage && (
          <div
            className={clsx(
              'text-[13px] truncate',
              unread > 0
                ? 'text-stone-800 dark:text-stone-200 font-medium'
                : 'text-stone-500 dark:text-stone-400',
            )}
          >
            {lastMessage}
          </div>
        )}
        <div className="text-[12px] text-stone-500 truncate flex items-center gap-1.5">
          {snoozedUntil ? <span>Snoozed {snoozedUntil}</span> : status.label && <span>{status.label}</span>}
          {uptime && <span className="text-stone-400">· up {uptime}</span>}
          {lastActivity && (
            <>
              <span className="text-stone-300">•</span>
              <span className="truncate">{lastActivity}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        {hasDraft && (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-white px-2 py-0.5 rounded-full bg-amber-600 dark:bg-amber-500 shadow-sm ring-1 ring-amber-700/40 dark:ring-amber-300/40"
            title="Unsent draft saved on this chat"
          >
            <PencilLine className="w-3 h-3" aria-hidden />
            Draft
          </span>
        )}
        {queue > 0 && (
          <span
            className="text-[10px] font-bold text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded bg-amber-100/60 dark:bg-amber-900/30"
            title="Queued messages waiting to be dispatched in this chat"
          >
            {queue} queued
          </span>
        )}
        <div className="flex flex-col items-end gap-1" aria-label="Chat badges">
          {unread > 0 && (
            <span
              className="text-[10px] font-bold text-white bg-blue-600 rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center"
              title={`${unread} unread ${chat.type === 'auto-spawn' ? `topic${unread === 1 ? '' : 's'}` : `message${unread === 1 ? '' : 's'}`}`}
              aria-label={`${unread} unread ${chat.type === 'auto-spawn' ? `topic${unread === 1 ? '' : 's'}` : `message${unread === 1 ? '' : 's'}`}`}
            >
              {unread}
            </span>
          )}
          {activeTopics > 0 && (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-950 bg-emerald-100 border border-emerald-300 rounded-full px-1.5 py-0.5 min-w-[1.25rem] justify-center dark:bg-emerald-900/60 dark:text-emerald-100 dark:border-emerald-700"
              title={`${activeTopics} active topic${activeTopics === 1 ? '' : 's'}`}
              aria-label={`${activeTopics} active topic${activeTopics === 1 ? '' : 's'}`}
            >
              <CheckSquare className="h-2.5 w-2.5" aria-hidden />
              {activeTopics}
            </span>
          )}
        </div>
      </div>
    </Link>
    {chat.type !== 'auto-spawn' && chat.scheduled_rechecks?.map((recheck) => (
      <Link
        key={recheck.topic_id}
        to={`/chats/${chat.id}?topic_id=${recheck.topic_id}`}
        className="block pl-14 pr-3 py-2 border-b border-stone-100 dark:border-stone-800 text-sm text-blue-700 dark:text-blue-300"
      >
        <span className="block truncate">{recheck.title || 'Scheduled follow-up'}</span>
        <span className="block text-xs text-stone-500">Scheduled check {relativeTimeFromNow(recheck.run_at)}</span>
      </Link>
    ))}
    </>
  )
}

function WorkMonitorRow({ item, engagedTopic }: { item: WorkMonitorItem; engagedTopic?: EngagedTopic }) {
  const prefetch = usePrefetchChat()
  const age = relativeUptime(item.started_at) || 'now'
  const href = item.topic_id
    ? `/chats/${item.chat_id}?topic_id=${item.topic_id}`
    : `/chats/${item.chat_id}`
  const status = activeWorkStatusFriendly(item.status, {
    startedAt: item.started_at,
    lastProgressAt: item.last_progress_at,
    lastProgressKind: item.last_progress_kind,
  })
  const statusTone = status.tone === 'blue'
    ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200'
    : 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200'

  return (
    <div className="flex items-center border-b border-stone-100 dark:border-stone-800/60">
      <Link
        to={href}
        data-lane-id={!item.topic_id ? item.chat_id : undefined}
        onPointerDown={() => prefetch(item.chat_id, item.topic_id)}
        onTouchStart={() => prefetch(item.chat_id, item.topic_id)}
        onMouseEnter={() => prefetch(item.chat_id, item.topic_id)}
        onFocus={() => prefetch(item.chat_id, item.topic_id)}
        className="flex min-w-0 flex-1 items-start gap-3 px-3 py-2.5 hover:bg-stone-100 dark:hover:bg-stone-800/60 transition-colors"
      >
        <div className="relative shrink-0 mt-0.5">
          <AgentBadge agent={item.agent} size={34} />
          <span
            className={clsx(
              'absolute -bottom-0.5 -right-0.5 inline-block w-2.5 h-2.5 rounded-full ring-2 ring-stone-50 dark:ring-stone-900',
              TONE_BG[status.tone],
              status.blink && 'animate-pulse',
            )}
            title={status.description}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-sm truncate">{item.title}</span>
            <span className="text-[9px] uppercase tracking-wider text-stone-500 px-1.5 py-0.5 bg-stone-200 dark:bg-stone-700 rounded shrink-0">
              {item.kind}
            </span>
          </div>
          {item.preview && (
            <div className="text-[13px] text-stone-500 dark:text-stone-400 truncate">
              {item.preview}
            </div>
          )}
          <div className="text-[11px] text-stone-500 truncate flex items-center gap-1.5">
            <span>{item.chat_display_name || item.chat_id}</span>
            <span className="text-stone-300">•</span>
            <span>{item.active_message_count} active</span>
            {item.kind === 'topic' && (
              <>
                <span className="text-stone-300">•</span>
                <span>{item.chat_worker_capacity} slots</span>
              </>
            )}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1">
          <span className={clsx('px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide', statusTone)}>
            {status.label || item.status}
          </span>
          <span className="text-[11px] text-stone-500 tabular-nums">{age}</span>
        </div>
      </Link>
      {engagedTopic && <EngagedTopicDoneButton topic={engagedTopic} />}
    </div>
  )
}

function LongWorkView({
  items,
  engagedTopics,
  summary,
  isLoading,
  error,
}: {
  items: WorkMonitorItem[]
  engagedTopics: EngagedTopic[]
  summary: WorkMonitor['summary'] | undefined
  isLoading: boolean
  error: unknown
}) {
  if (isLoading && !summary) {
    return <div className="p-6 text-stone-500 text-sm">Loading work…</div>
  }
  if (error && !summary) {
    return <div className="p-6 text-red-600 text-sm">Failed to load work: {String((error as Error).message)}</div>
  }

  return (
    <section>
      {summary && (
        <div className="px-3 py-2 text-[11px] text-stone-500 border-b border-stone-100 dark:border-stone-800/60 flex items-center gap-2">
          <Clock3 className="w-3.5 h-3.5" aria-hidden />
          <span>{summary.dispatched_message_count} working</span>
          <span className="text-stone-300">•</span>
          <span>{summary.queued_message_count} queued</span>
          <span className="text-stone-300">•</span>
          <span>{summary.worker_capacity} slots</span>
        </div>
      )}
      {items.length === 0 ? (
        <div className="grid h-[50vh] place-items-center px-6">
          <div className="text-center max-w-md">
            <MessageSquare className="w-10 h-10 mx-auto mb-3 text-stone-400" />
            <div className="text-sm font-medium mb-1">No active work</div>
            <div className="text-xs text-stone-500">No items have been running for more than 30 minutes.</div>
          </div>
        </div>
      ) : (
        items.map((item) => <WorkMonitorRow
          key={`${item.kind}:${item.topic_id || item.chat_id}`}
          item={item}
          engagedTopic={engagedTopics.find((topic) => topic.id === item.topic_id && topic.chat_id === item.chat_id)}
        />)
      )}
    </section>
  )
}

export default function ChatListPage() {
  const { data, isLoading, error } = useChats({ warmReadModels: true })
  const { data: setupManifest } = useSetupManifest()
  const wsConnected = useWsConnected()
  // Undelivered outbox work per chat — a chat holding one is live, never
  // finished/free, even though the server sees no queued row for it yet.
  const { chatIds: outboxChatIds, topicKeys: outboxTopicKeys } = useOutboxPendingScopes()
  const prefetchChat = usePrefetchChat()
  const draftsByChat = useUi((s) => s.draftsByChat)
  const draftUpdatedAt = useUi((s) => s.draftUpdatedAt)
  const pendingShare = useUi((s) => s.pendingShare)
  const setPendingShare = useUi((s) => s.setPendingShare)
  const qc = useQueryClient()
  const [allView, setAllView] = useState<AllView>(loadAllView)
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  // The active filter tab lives in the URL (?tab=groups; Lanes is the default
  // home with no param). Keeping it in the URL is exactly what lets the back
  // button resolve "Groups tab → Lanes" via the shared backTarget() — the URL
  // is the single source of truth the hardware back reads. See chatNavigation.ts.
  const hideGroups = setupManifest?.ui?.hide_groups === true
  const configuredVisibleGroupIds = setupManifest?.ui?.visible_group_ids ?? []
  const showGroupsTab = !hideGroups || configuredVisibleGroupIds.length > 0
  const tabParam = searchParams.get('tab')
  // Code is its own tab, independent of the Groups visibility switch: hiding
  // Groups is about the automation inboxes, not Mark's coding surface.
  const tab: FilterTab = tabParam === 'code'
    ? 'code'
    : showGroupsTab && tabParam === 'groups' ? 'groups' : 'lanes'
  // The active chat filter also lives in the URL (?filter=, mirroring ?tab=) so
  // the hardware back button can read it and collapse the list down to Done (see
  // chatNavigation.ts backTarget). When the URL carries no filter yet — e.g.
  // backing out of a chat onto the bare Lanes home — fall back to the last filter
  // picked for this tab so the first paint is already filtered (no unfiltered
  // flash); the restore effect below then reflects it into the URL.
  const filterParam = searchParams.get('filter')
  const filter: ChatFilter = filterParam == null
    ? loadStoredFilter(tab)
    : normalizeFilter(tab, filterParam)
  const showingLongWork = allView === 'long-work' && filter === 'all'
  const {
    data: workMonitor,
    isLoading: workMonitorLoading,
    error: workMonitorError,
  } = useWorkMonitor()
  const allChats = useMemo(() => {
    const chats = data ?? []
    if (!hideGroups) return chats
    const visible = new Set(configuredVisibleGroupIds)
    return chats.filter((chat) => chat.type !== 'auto-spawn' || visible.has(chat.id))
  }, [configuredVisibleGroupIds, data, hideGroups])
  const engagedTopics = useEngagedTopics(tab === 'lanes')
  const visibleEngagedTopics = useMemo(() => {
    const visibleIds = new Set(allChats.map((chat) => chat.id))
    return (engagedTopics.data ?? []).filter((topic) => visibleIds.has(topic.chat_id))
  }, [allChats, engagedTopics.data])
  const engagedTopicsByFilter = useMemo(() => {
    const matches = (view: ChatFilter) => visibleEngagedTopics.filter((topic) => (
      matchesEngagedTopicFilter(topic, view, outboxTopicKeys.has(outboxTopicKey(topic.chat_id, topic.id)))
    ))
    return { all: matches('all'), done: matches('done'), live: matches('live'), snooze: matches('snooze'), free: [] }
  }, [outboxTopicKeys, visibleEngagedTopics])
  const selectedGroup = useMemo(
    () => allChats.find((chat) => chat.id === selectedGroupId && chat.type === 'auto-spawn') ?? null,
    [allChats, selectedGroupId],
  )
  const selectedGroupUnread = selectedGroup?.unread_topic_count ?? 0
  const markSelectedGroupRead = useMarkChatRead(selectedGroup?.id)

  useEffect(() => {
    if (!selectedGroupId) return
    if (!data) return
    if (!selectedGroup) setSelectedGroupId(null)
  }, [data, selectedGroup, selectedGroupId])

  // Restore the last-used filter when the list is shown without an explicit
  // ?filter= — e.g. backing out of a chat resolves to the bare Lanes home. This
  // preserves "whichever filter was there last" while keeping the URL the single
  // source of truth the back button reads. Setting the param makes searchParams
  // carry it, so the effect no-ops on the next run (no loop); a remembered 'all'
  // leaves the URL clean.
  useEffect(() => {
    // In split view this component also lives beside /chats/:chatId. Never
    // rewrite the open chat URL merely to restore the left pane's remembered
    // filter; the filter already falls back to localStorage below. Otherwise
    // every row click becomes two navigations (open chat, then add ?filter=),
    // causing avoidable list-wide rerenders.
    if (location.pathname !== '/chats') return
    if (searchParams.has('filter')) return
    const restored = loadStoredFilter(tab)
    if (restored === 'all') return
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      p.set('filter', restored)
      return p
    }, { replace: true })
  }, [location.pathname, tab, searchParams, setSearchParams])

  // Remember whichever filter the list is SHOWING, however it got there — a tap,
  // a deep link, or Back collapsing Live → Done. Opening a lane and backing out
  // lands on the bare list, which must restore the view Mark left, not the last
  // chip he tapped.
  useEffect(() => {
    if (location.pathname !== '/chats') return
    if (filterParam == null) return
    saveStoredFilter(tab, normalizeFilter(tab, filterParam))
  }, [location.pathname, tab, filterParam])

  // Web Share Target intake — sw.js stores the shared payload in IDB and
  // 303-redirects here with ?share=<id>. Pull the row, hold it in the
  // store as a pendingShare snapshot (so navigating to a chat keeps it
  // in-flight), and strip the param so a refresh doesn't double-attach.
  useEffect(() => {
    const shareParam = searchParams.get('share')
    if (!shareParam) return
    const id = Number(shareParam)
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      p.delete('share')
      return p
    }, { replace: true })
    if (Number.isNaN(id)) return
    void peekPendingShare(id).then((share) => {
      if (share && (share.files.length > 0 || share.text)) {
        setPendingShare({ id: share.id, files: share.files, text: share.text })
      } else {
        // No row, or it expired — drop the IDB record so we don't keep
        // re-loading nothing. Banner stays hidden.
        void discardPendingShare(id)
      }
    })
    // Run once per ?share= landing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function dismissPendingShare() {
    if (pendingShare) void discardPendingShare(pendingShare.id)
    setPendingShare(null)
  }
  // Stretch the chat-row Links (skip the sticky header — it'd look broken
  // if translated since it's pinned to the top of the scroll container).
  useElasticOverscroll(scrollRef, { rowSelector: 'a[href^="/chats/"]' })
  // Pull-to-refresh on Android: drag down past 64px from the top → spinner
  // → invalidate the chats list (which triggers an immediate refetch).
  // Also nudges lanes so the status pills get fresh liveness/progress data.
  const pull = usePullToRefresh(scrollRef, async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['chats'] }),
      qc.invalidateQueries({ queryKey: ['lanes'] }),
      qc.invalidateQueries({ queryKey: ['engaged-topics'] }),
    ])
  })

  // One selector for every work-state filter: each tab's badge and its list
  // are both read from here (chats plus, on Lanes, joined topics), so a badge
  // can never count something the tab does not show, or the reverse.
  const filterItems = useMemo(() => {
    const tabChats = filterChatsByTab(data ?? [], tab)
    const activeTabChats = tabChats.filter((c) => !isSnoozedChat(c))
    const topicsFor = (view: ChatFilter) => (tab === 'lanes' ? engagedTopicsByFilter[view] : [])
    const items: Record<ChatFilter, { chats: Chat[]; topics: EngagedTopic[] }> = {
      all: { chats: activeTabChats, topics: topicsFor('all') },
      done: { chats: tabChats.filter((c) => matchesDoneChatFilter(c, outboxChatIds.has(c.id))), topics: topicsFor('done') },
      live: { chats: activeTabChats.filter((c) => hasActiveChatWork(c, outboxChatIds.has(c.id))), topics: topicsFor('live') },
      free: { chats: activeTabChats.filter((c) => isFreeChat(c, wsConnected, outboxChatIds.has(c.id))), topics: [] },
      snooze: { chats: tabChats.filter(isSnoozedChat), topics: topicsFor('snooze') },
    }
    return items
  }, [data, engagedTopicsByFilter, outboxChatIds, tab, wsConnected])

  const counts = useMemo(() => {
    const all = data?.length ?? 0
    const code = data?.filter((c) => isCodeChatId(c.id)).length ?? 0
    const groups = (data?.filter((c) => c.type === 'auto-spawn').length ?? 0) - code
    // The TODO tab opens the Todoist group directly, so its badge is that
    // group's unread count (its "needs attention" signal), not a tab item count.
    const todoUnread = hideGroups ? 0 : (data?.find((c) => c.id === TODO_CHAT_ID)?.unread ?? 0)
    const size = (view: ChatFilter): number => filterItems[view].chats.length + filterItems[view].topics.length
    return {
      all,
      lanes: all - groups - code,
      todoUnread,
      groups,
      code,
      finished: size('done'),
      active: size('live'),
      // Work-state filters are lane-only; other tabs never show these badges.
      free: tab === 'lanes' ? size('free') : 0,
      snoozed: tab === 'lanes' ? size('snooze') : 0,
    }
  }, [data, filterItems, hideGroups, tab])

  // Filters are mutually exclusive and derived straight from the URL. All
  // work-state filters are lane-only; Groups normalizes every one to 'all'.
  const finishedOnly = filter === 'done'
  const runningOnly = filter === 'live'
  const freeOnly = filter === 'free'
  const snoozedOnly = filter === 'snooze'

  const resetListScroll = (): void => {
    const list = scrollRef.current
    if (!list) return
    list.scrollTo({ top: 0, behavior: 'auto' })
    // The filter/tab URL update rerenders the row set. Repeat after that paint
    // so browser scroll anchoring cannot preserve a now-stale first row and
    // hide a newly inserted lane immediately above the viewport.
    requestAnimationFrame(() => list.scrollTo({ top: 0, behavior: 'auto' }))
  }

  // Selecting a filter writes it to the URL (the live source of truth the back
  // button reads) and remembers it for this tab. Tapping the active filter again
  // clears back to "all". One handler covers all four buttons.
  const applyFilter = (next: ChatFilter): void => {
    const target: ChatFilter = filter === next ? 'all' : next
    setAllView('chats')
    saveAllView('chats')
    resetListScroll()
    saveStoredFilter(tab, target)
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      if (target === 'all') p.delete('filter')
      else p.set('filter', target)
      return p
    }, { replace: true })
  }

  const toggleAllView = (): void => {
    const next = showingLongWork ? 'chats' : 'long-work'
    setAllView(next)
    saveAllView(next)
    resetListScroll()
    if (next === 'long-work') {
      saveStoredFilter(tab, 'all')
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev)
        p.delete('filter')
        return p
      }, { replace: true })
    }
  }

  function selectGroup(chat: Chat): void {
    if (chat.type !== 'auto-spawn') return
    setSelectedGroupId(chat.id)
  }

  function toggleSelectedGroup(chat: Chat): void {
    if (chat.type !== 'auto-spawn') return
    setSelectedGroupId((current) => (current === chat.id ? null : chat.id))
  }

  async function markSelectedRead(): Promise<void> {
    if (!selectedGroup || selectedGroupUnread === 0 || markSelectedGroupRead.isPending) return
    await markSelectedGroupRead.mutateAsync({ allTopics: true })
    setSelectedGroupId(null)
  }

  const filteredByTab = useMemo(() => filterChatsByTab(data ?? [], tab), [data, tab])

  const longWorkItems = useMemo(() => {
    if (!workMonitor) return []
    const thirtyMinAgo = workMonitor.now - 30 * 60
    const old = workMonitor.items.filter((item) => item.started_at <= thirtyMinAgo)
    const tabChatIds = new Set(filteredByTab.map((c) => c.id))
    return old.filter((item) => tabChatIds.has(item.chat_id)
      || (tab === 'lanes' && isLongEngagedTopicWork(item, visibleEngagedTopics)))
  }, [workMonitor, filteredByTab, tab, visibleEngagedTopics])
  const showLongWorkFilter = longWorkItems.length > 0 || showingLongWork
  const chatFilterGridClass = tab === 'lanes'
    ? (showLongWorkFilter ? 'grid-cols-5' : 'grid-cols-4')
    : 'grid-cols-1'

  const filteredChats = filterItems[filter].chats
  const filterTopics = filterItems[filter].topics

  function pickTab(t: FilterTab): void {
    if (!showGroupsTab && t === 'groups') return
    if (t === 'code' && counts.code === 0) return
    setSelectedGroupId(null)
    resetListScroll()
    // Write the tab into the URL so it's the single source of truth — this is
    // what makes the back button able to step Groups → Lanes. Restore that
    // tab's last-used filter alongside it (each tab remembers its own).
    const restored = loadStoredFilter(t)
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      p.set('tab', t)
      if (restored === 'all') p.delete('filter')
      else p.set('filter', restored)
      return p
    }, { replace: true })
  }

  const chats = useMemo(
    () => prioritizeDraftChats(filteredChats, draftsByChat, draftUpdatedAt),
    [filteredChats, draftsByChat, draftUpdatedAt],
  )
  const laneTopicsVisible = tab === 'lanes' && !freeOnly && !showingLongWork
    && engagedTopicsByFilter[filter].length > 0
  const visibleGroupIds = useMemo(
    () => chats
      .filter((chat) => chat.type === 'auto-spawn')
      .slice(0, 8)
      .map((chat) => chat.id),
    [chats],
  )

  useEffect(() => {
    if (visibleGroupIds.length === 0) return
    const id = window.setTimeout(() => {
      for (const chatId of visibleGroupIds) {
        prefetchChat(chatId, null, { messages: false, topicMessages: 3 })
      }
    }, 75)
    return () => window.clearTimeout(id)
  }, [prefetchChat, visibleGroupIds])

  useEffect(() => {
    if (chats.length === 0) return
    const id = window.setTimeout(preloadThreadViewComponents, 175)
    return () => window.clearTimeout(id)
  }, [chats.length])

  const { archivedIds, setArchived, collapsed: archiveCollapsed, setCollapsed: setArchiveCollapsed, syncError: archiveSyncError } = useArchivedGroups()
  const [dragZone, setDragZone] = useState<GroupZone | null>(null)
  const groupsTab = tab === 'groups' && !showingLongWork
  const isArchived = (c: Chat) => groupsTab && archivedIds.includes(c.id)
  const draftChats = chats.filter((c) => chatHasDraft(draftsByChat, c.id))
  const pinned = chats.filter((c) => !chatHasDraft(draftsByChat, c.id) && c.pinned && !isArchived(c))
  const rest = chats.filter((c) => !chatHasDraft(draftsByChat, c.id) && !c.pinned && !isArchived(c))
  const archived = chats.filter((c) => !chatHasDraft(draftsByChat, c.id) && isArchived(c))
  const renderGroupRow = (c: Chat, zone: GroupZone) => {
    const row = (
      <ChatRow
        key={c.id}
        chat={c}
        draft={firstDraftForChat(draftsByChat, c.id)}
        selectionMode={!!selectedGroup}
        selected={selectedGroup?.id === c.id}
        onSelectGroup={selectGroup}
        onToggleGroup={toggleSelectedGroup}
      />
    )
    if (!groupsTab) return row
    return (
      <DraggableGroupRow
        key={c.id}
        zone={zone}
        onDragZone={setDragZone}
        onDrop={(target) => setArchived(c.id, target === 'archive')}
      >
        {row}
      </DraggableGroupRow>
    )
  }

  // Capture the rendered order, including Drafts and Pinned, for keyboard
  // navigation in both split view and an expanded full-window lane.
  const laneOrder = JSON.stringify(showingLongWork
    ? longWorkItems.filter((item) => !item.topic_id).map((item) => item.chat_id)
    : [...draftChats, ...pinned, ...rest].map((c) => c.id))
  useEffect(() => {
    if (tab === 'lanes') saveLaneNavigationOrder(JSON.parse(laneOrder))
  }, [laneOrder, tab])

  // WhatsApp-grade reliability: only the FIRST EVER load shows a
  // loading spinner. Subsequent reloads return cached chats from
  // localStorage instantly, and TanStack revalidates in the
  // background. The user never sees a blank page after their first
  // successful load.
  if (isLoading && !data) {
    return (
      <div className="p-6 text-stone-500 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-stone-400 animate-pulse" />
        Loading lanes…
      </div>
    )
  }

  // WhatsApp-grade reliability rule #1: NEVER nuke the chat list
  // because of a transient backend hiccup. A 500 response, a network
  // blip, a Caddy restart, a controller restart — all of these used
  // to flip the screen to "Couldn't reach the controller" and force
  // the user to refresh. Now: we only show the full-page error if
  // the user has NO cached chat list to fall back on (first-ever
  // visit). Otherwise we render the cached chats and surface a small
  // amber pill at the top of the header (rendered below) so the user
  // knows something's off without being blocked.
  //
  // Auth errors still get the full-page treatment because cached
  // chats won't help — the JWT cookie needs renewal.
  const errorMsg = error ? String((error as Error).message ?? '') : ''
  const isAuthError =
    !!error
    && /\b(401|403|unauthor|forbid|not\s+in\s+allowlist)/i.test(errorMsg)
  const hasCachedData = (data?.length ?? 0) > 0
  const showFullErrorPage = !!error && (!hasCachedData || isAuthError)
  const isOffline = typeof navigator !== 'undefined' && !navigator.onLine

  if (showFullErrorPage) {
    return (
      <div className="p-8 max-w-md mx-auto text-center">
        <div className="text-5xl mb-4">{isAuthError ? '🔒' : '⚠️'}</div>
        <h2 className="text-lg font-semibold mb-2">
          {isAuthError ? 'You need to log in' : (isOffline ? 'Offline with no local copy' : 'Couldn’t reach the controller')}
        </h2>
        <p className="text-sm text-stone-500 mb-4">
          {isAuthError
            ? (AUTH_HELP_URL
                ? 'Open this site once via your trusted URL to set up your secure session, then this URL works everywhere.'
                : 'Log in to continue.')
            : (isOffline
                ? 'Open AgentChat once while online to cache your chats on this device.'
                : 'The backend isn’t responding. Try refresh — if it persists, the controller may be restarting.')}
        </p>
        {isAuthError && AUTH_HELP_URL && (
          <a
            href={AUTH_HELP_URL}
            className="inline-block text-sm px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            Open trusted URL →
          </a>
        )}
        <p className="text-[11px] text-stone-400 mt-4 font-mono break-all">{errorMsg}</p>
      </div>
    )
  }

  if (allChats.length === 0) {
    return (
      <div className="grid h-full place-items-center px-6">
        <div className="text-center max-w-md">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 text-stone-400" />
          <div className="text-lg font-medium mb-1">No lanes yet</div>
          <div className="text-sm text-stone-500">
            Run <code className="px-1 py-0.5 text-xs bg-stone-100 dark:bg-stone-800 rounded">lane_spawner --pwa-defaults</code> on the VPS to create them.
          </div>
        </div>
      </div>
    )
  }

  const tabBtnCls = (active: boolean) =>
    clsx(
      'flex-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
      active
        ? 'bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900'
        : 'text-stone-600 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800/60',
    )

  return (
    /* `relative` wrapper OUTSIDE the scroller is the positioning context for
       the Lanes and Code tabs' compose buttons. They used to be `fixed`, which anchors
       to the viewport — fine on a phone, wrong in AppShell's split layout,
       where this list is a 360/380px left pane and the buttons landed in the
       bottom-right of the whole window, over the open chat. Anchoring them
       here keeps them inside the pane in both layouts, and because the
       containing block sits above the scroller they still don't scroll away. */
    <div className="relative h-full">
    <div ref={scrollRef} className="h-full overflow-y-auto">
      {/* Pull-to-refresh indicator — sits ABOVE the sticky header so it
          rubber-bands the entire view, mirroring WhatsApp / Gmail.
          Renders nothing when distance===0 + state==='idle'. */}
      <PullIndicator distance={pull.distance} state={pull.state} />
      <header className="px-3 py-3 border-b border-stone-200 dark:border-stone-800 sticky top-0 bg-stone-50/95 dark:bg-stone-900/95 backdrop-blur z-10">
        <div className="flex items-center gap-2 mb-2">
          <Inbox className="w-5 h-5" aria-hidden />
          <h1 className="text-lg font-semibold">AgentChat</h1>
          {/* Reconnecting indicator — only shows when we have cached
              chats AND the most-recent fetch failed. Tiny, non-blocking;
              the cached list still renders below. TanStack auto-retries
              in the background. */}
          {error && hasCachedData && (
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full
                         bg-amber-100 text-amber-800 border border-amber-300
                         inline-flex items-center gap-1"
              title={`Reconnecting — ${errorMsg}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              Reconnecting
            </span>
          )}
          <span className="text-xs text-stone-500 ml-auto">{counts.all} chats</span>
        </div>
        {selectedGroup ? (
          <div className="mt-2 flex items-center gap-2 rounded-xl bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 px-2 py-2">
            <button
              type="button"
              onClick={() => setSelectedGroupId(null)}
              className="grid place-items-center w-8 h-8 rounded-lg text-blue-900/80 dark:text-blue-100/80 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
              aria-label="Clear selected group"
              title="Clear selection"
            >
              <X className="w-4 h-4" />
            </button>
            <span className="flex-1 min-w-0 text-xs font-semibold text-blue-950 dark:text-blue-100 truncate">
              {selectedGroup.display_name || selectedGroup.id}
            </span>
            <button
              type="button"
              onClick={() => void markSelectedRead()}
              disabled={selectedGroupUnread === 0 || markSelectedGroupRead.isPending}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors',
                selectedGroupUnread === 0 || markSelectedGroupRead.isPending
                  ? 'bg-stone-200 text-stone-500 dark:bg-stone-800 dark:text-stone-400'
                  : 'bg-blue-600 text-white hover:bg-blue-700',
              )}
              aria-label="Mark selected group as read"
              title={selectedGroupUnread === 0 ? 'No unread topics' : 'Mark every topic in this group as read'}
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark read{selectedGroupUnread > 0 ? ` (${selectedGroupUnread})` : ''}
            </button>
          </div>
        ) : (
          <>
            <nav className="flex gap-1 p-0.5 rounded-xl bg-stone-100 dark:bg-stone-800/50" aria-label="Filter chats">
              <button
                type="button"
                onClick={() => pickTab('lanes')}
                className={tabBtnCls(tab === 'lanes')}
              >
                Lanes <span className="text-[10px] opacity-70 ml-0.5">{counts.lanes}</span>
              </button>
              {/* Code sits immediately after Lanes: it is Mark's coding surface,
                  not one of the automation inboxes, so it is deliberately not
                  gated behind the Groups visibility switch. */}
              {counts.code > 0 && (
                <button
                  type="button"
                  onClick={() => pickTab('code')}
                  className={tabBtnCls(tab === 'code')}
                >
                  Code <span className="text-[10px] opacity-70 ml-0.5">{counts.code}</span>
                </button>
              )}
              {showGroupsTab && (
                <>
                  {/* TODO is a shortcut, not a list filter: it opens the Todoist
                      group's topics directly (one tap, no intermediate one-row
                      list). It's never an "active" tab — tapping navigates away. */}
                  {!hideGroups && (
                    <button
                      type="button"
                      onPointerDown={() => prefetchChat(TODO_CHAT_ID, null, { messages: false, topicMessages: 3 })}
                      onMouseEnter={() => prefetchChat(TODO_CHAT_ID, null, { messages: false, topicMessages: 3 })}
                      onFocus={() => prefetchChat(TODO_CHAT_ID, null, { messages: false, topicMessages: 3 })}
                      onClick={() => navigate(chatTarget(TODO_CHAT_ID))}
                      className={tabBtnCls(false)}
                    >
                      TODO{counts.todoUnread > 0 && (
                        <span className="text-[10px] opacity-70 ml-0.5">{counts.todoUnread}</span>
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => pickTab('groups')}
                    className={tabBtnCls(tab === 'groups')}
                  >
                    Groups <span className="text-[10px] opacity-70 ml-0.5">{counts.groups}</span>
                  </button>
                </>
              )}
            </nav>
            {(tab === 'lanes' || showLongWorkFilter) && <div
              className={clsx(
                'mt-2 grid w-full items-center gap-1',
                chatFilterGridClass,
              )}
              data-chat-filter-row
            >
              {tab === 'lanes' && (
                <button
                  type="button"
                  onClick={() => applyFilter('done')}
                  title={finishedOnly ? 'Show all chats' : 'Show finished chats, unread lane messages and idle topics you have replied to'}
                  aria-pressed={finishedOnly}
                  className={clsx(
                    'inline-flex min-w-0 items-center justify-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors',
                    finishedOnly
                      ? 'bg-emerald-600 text-white'
                      : counts.finished === 0
                        ? 'cursor-not-allowed text-stone-400 dark:text-stone-600'
                        : 'text-stone-600 dark:text-stone-300 hover:bg-stone-200/70 dark:hover:bg-stone-800/70',
                  )}
                >
                  Done <span className="text-[10px] opacity-75">{counts.finished}</span>
                </button>
              )}
              {tab === 'lanes' && (
                <button
                  type="button"
                  onClick={() => applyFilter('free')}
                  disabled={counts.free === 0 && !freeOnly}
                  title={freeOnly ? 'Show used and running lanes too' : 'Show only unused or reset lanes that are ready for a new message'}
                  aria-pressed={freeOnly}
                  className={clsx(
                    'inline-flex min-w-0 items-center justify-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors',
                    freeOnly
                      ? 'bg-emerald-600 text-white'
                      : counts.free === 0
                        ? 'cursor-not-allowed text-stone-400 dark:text-stone-600'
                        : 'text-stone-600 dark:text-stone-300 hover:bg-stone-200/70 dark:hover:bg-stone-800/70',
                  )}
                >
                  Free <span className="text-[10px] opacity-75">{counts.free}</span>
                </button>
              )}
              {tab === 'lanes' && (
                <button
                  type="button"
                  onClick={() => applyFilter('live')}
                  disabled={counts.active === 0 && !runningOnly}
                  title={runningOnly ? 'Show idle and finished chats too' : 'Show only chats with active or queued work'}
                  aria-pressed={runningOnly}
                  className={clsx(
                    'inline-flex min-w-0 items-center justify-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors',
                    runningOnly
                      ? 'bg-blue-600 text-white'
                      : counts.active === 0
                        ? 'cursor-not-allowed text-stone-400 dark:text-stone-600'
                        : 'text-stone-600 dark:text-stone-300 hover:bg-stone-200/70 dark:hover:bg-stone-800/70',
                  )}
                >
                  Live <span className="text-[10px] opacity-75">{counts.active}</span>
                </button>
              )}
              {tab === 'lanes' && (
                <button
                  type="button"
                  onClick={() => applyFilter('snooze')}
                  disabled={counts.snoozed === 0 && !snoozedOnly}
                  title={snoozedOnly ? 'Show active lanes again' : 'Show only lanes hidden until a future time'}
                  aria-pressed={snoozedOnly}
                  className={clsx(
                    'inline-flex min-w-0 items-center justify-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors',
                    snoozedOnly
                      ? 'bg-amber-600 text-white'
                      : counts.snoozed === 0
                        ? 'cursor-not-allowed text-stone-400 dark:text-stone-600'
                        : 'text-stone-600 dark:text-stone-300 hover:bg-stone-200/70 dark:hover:bg-stone-800/70',
                  )}
                >
                  Snooze <span className="text-[10px] opacity-75">{counts.snoozed}</span>
                </button>
              )}
              {showLongWorkFilter && (
                <button
                  type="button"
                  onClick={toggleAllView}
                  title={showingLongWork ? 'Show normal chat list' : 'Show work running over 30 minutes'}
                  aria-pressed={showingLongWork}
                  className={clsx(
                    'inline-flex min-w-0 items-center justify-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors',
                    showingLongWork
                      ? 'bg-blue-600 text-white'
                      : 'text-stone-600 dark:text-stone-300 hover:bg-stone-200/70 dark:hover:bg-stone-800/70',
                  )}
                >
                  Long
                  <span className="text-[10px] opacity-75">{longWorkItems.length}</span>
                </button>
              )}
            </div>}
          </>
        )}
      </header>

      {/* Pending Web-Share-Target banner. Tapping any chat row carries the
          snapshot through; the Composer consumes it on mount. */}
      {pendingShare && (
        <div
          role="status"
          className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 dark:bg-blue-950/40 border-b border-blue-200 dark:border-blue-900 text-blue-900 dark:text-blue-100"
        >
          {pendingShare.files.length > 0 ? (
            <Paperclip className="w-4 h-4 shrink-0" aria-hidden />
          ) : (
            <MessageSquare className="w-4 h-4 shrink-0" aria-hidden />
          )}
          <span className="text-[13px] font-medium flex-1 truncate">
            {pendingShareBannerText(pendingShare)}
          </span>
          <button
            type="button"
            onClick={dismissPendingShare}
            className="grid place-items-center w-8 h-8 rounded-full text-blue-900/80 dark:text-blue-100/80 hover:bg-blue-100 dark:hover:bg-blue-900/60"
            aria-label="Dismiss share"
            title="Discard"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {showingLongWork ? (
        <LongWorkView
          items={longWorkItems}
          engagedTopics={tab === 'lanes' ? visibleEngagedTopics : []}
          summary={workMonitor?.summary}
          isLoading={workMonitorLoading}
          error={workMonitorError}
        />
      ) : tab === 'code' ? (
        /* Code is a conversation list, not a chat list: it flattens the topics
           of every coding group into one inbox. See CodeConversationsView. */
        <CodeConversationsView />
      ) : (
        <>
          {/* Lanes counts include joined topics (engagedTopicsByFilter), and
              the topics list renders below this block. Only show the empty
              state when that list is empty too, so the count and the body
              agree (a full-height empty card used to hide a live topic). */}
          {chats.length === 0 && !finishedOnly && !laneTopicsVisible && (
            <div className="grid h-full place-items-center px-6 py-12">
              <div className="text-center max-w-md">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 text-stone-400" />
                <div className="text-lg font-medium mb-1">
                  No {tab === 'lanes' ? 'lanes' : tab === 'groups' ? 'groups' : 'chats'} in this tab
                </div>
                <div className="text-sm text-stone-500">
                  {runningOnly
                    ? 'No running chats in this tab.'
                    : finishedOnly
                      ? 'No finished chats or unread messages in this tab.'
                      : freeOnly
                        ? 'No free lanes in this tab.'
                        : snoozedOnly
                          ? 'No snoozed lanes.'
                          : 'Switch to a different tab above to see other chats.'}
                </div>
              </div>
            </div>
          )}

          {draftChats.length > 0 && (
            <section>
              <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-amber-900 dark:text-amber-200 font-bold bg-amber-100/80 dark:bg-amber-900/30 border-y border-amber-300/60 dark:border-amber-700/40 flex items-center gap-1.5">
                <PencilLine className="w-3.5 h-3.5" aria-hidden />
                Drafts
                <span className="text-amber-700 dark:text-amber-300 font-normal normal-case tracking-normal">
                  · {draftChats.length}
                </span>
              </div>
              {draftChats.map((c) => (
                <ChatRow
                  key={c.id}
                  chat={c}
                  draft={firstDraftForChat(draftsByChat, c.id)}
                  selectionMode={!!selectedGroup}
                  selected={selectedGroup?.id === c.id}
                  onSelectGroup={selectGroup}
                  onToggleGroup={toggleSelectedGroup}
                />
              ))}
            </section>
          )}

          {pinned.length > 0 && (
            <section>
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-stone-500">
                Pinned
              </div>
              {pinned.map((c) => renderGroupRow(c, 'main'))}
            </section>
          )}

          <section
            data-group-zone={groupsTab ? 'main' : undefined}
            className={clsx(dragZone === 'main' && 'bg-blue-50 dark:bg-blue-950/30 ring-2 ring-inset ring-blue-400')}
          >
            {(draftChats.length > 0 || pinned.length > 0) && (
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-stone-500">
                All chats
              </div>
            )}
            {rest.map((c) => renderGroupRow(c, 'main'))}
            {groupsTab && rest.length === 0 && (
              <div className="px-3 py-4 text-xs text-stone-400 text-center">Drag a group here to unarchive it</div>
            )}
          </section>

          {tab === 'lanes' && !freeOnly && (
            <EngagedTopicsView
              topics={filterTopics}
              loading={engagedTopics.isLoading}
              error={engagedTopics.isError}
              onRetry={() => { void engagedTopics.refetch() }}
            />
          )}

          {groupsTab && archiveSyncError && (
            <p role="status" className="px-4 py-2 text-xs text-amber-700 dark:text-amber-400">
              Archive sync unavailable. Changes are saved here and will retry automatically.
            </p>
          )}
          {groupsTab && (
            <section
              data-group-zone="archive"
              className={clsx(
                'pb-16',
                dragZone === 'archive' && 'bg-blue-50 dark:bg-blue-950/30 ring-2 ring-inset ring-blue-400',
              )}
            >
              <button
                type="button"
                onClick={() => setArchiveCollapsed(!archiveCollapsed)}
                aria-expanded={!archiveCollapsed}
                className="w-full flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-wider text-stone-500 border-y border-stone-200 dark:border-stone-800 hover:bg-stone-100 dark:hover:bg-stone-800/60"
              >
                {archiveCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                <Archive className="w-3.5 h-3.5" aria-hidden />
                Archive
                <span className="font-normal normal-case tracking-normal">· {archived.length}</span>
                {archiveCollapsed && archived.some((c) => (c.active_unread_topic_count ?? 0) > 0) && (
                  <span className="ml-auto w-2 h-2 rounded-full bg-blue-500" aria-label="Unread in archive" />
                )}
              </button>
              {!archiveCollapsed && archived.map((c) => renderGroupRow(c, 'archive'))}
              {!archiveCollapsed && archived.length === 0 && (
                <div className="px-3 py-4 text-xs text-stone-400 text-center">Drag a group here to archive it</div>
              )}
            </section>
          )}
        </>
      )}
    </div>
      {(tab === 'lanes' || tab === 'code') && counts.code > 0 && (
        <CodeConversationButtons parentTab={tab} />
      )}
    </div>
  )
}
